import React, { useState } from 'react';
import BrandLogo from './BrandLogo';
import './PrinterCard.css';
import axiosInstance from '../utils/axiosConfig';

import { FaSync, FaMapMarkerAlt, FaBarcode, FaExternalLinkAlt, FaPrint, FaTint, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const CopyableField = ({ value, icon = null }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;

    const copyToClipboard = (text) => {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text);
      }

      return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          document.body.removeChild(textarea);
          resolve();
        } catch (err) {
          document.body.removeChild(textarea);
          reject(err);
        }
      });
    };

    copyToClipboard(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    }).catch(err => {
      console.error('Error al copiar:', err);
    });
  };

  return (
    <span
      className={`copyable-field ${copied ? 'copied' : ''}`}
      onClick={handleCopy}
      title="Click para copiar"
    >
      {icon && icon}
      {value || 'N/A'}
    </span>
  );
};

const EditableField = ({ value, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    setCurrentValue(e.target.value);
  };

  const handleBlur = () => {
    onSave(currentValue);
    setIsEditing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSave(currentValue);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        type="number"
        value={currentValue || ''}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        autoFocus
        className="editable-field-input"
      />
    );
  }

  return (
    <span onDoubleClick={handleDoubleClick} className="editable-field" title="Doble click para editar">
      {value || 'N/A'}
    </span>
  );
};

const PrinterCard = ({ printer, onSave, onGetCounter }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [supplies, setSupplies] = useState([]);
  const [loadingSupplies, setLoadingSupplies] = useState(false);
  const [showSupplies, setShowSupplies] = useState(false);
  const [suppliesError, setSuppliesError] = useState(null);

  const stringToColor = (str) => {
    if (!str) return { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash % 360);
    const saturation = 65 + (Math.abs(hash >> 8) % 20);
    const lightness = 85 + (Math.abs(hash >> 16) % 10);

    return {
      bg: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
      color: `hsl(${hue}, ${saturation + 20}%, 30%)`,
      border: `hsl(${hue}, ${saturation}%, ${lightness - 15}%)`
    };
  };

  const sedeColors = stringToColor(printer.sede);

  const handleSyncCounter = async () => {
    setIsSyncing(true);
    try {
      await onGetCounter(printer.ip, printer.id);
    } finally {
      setTimeout(() => setIsSyncing(false), 1000);
    }
  };

  const handleIpClick = () => {
    const url = printer.url || `http://${printer.ip}/`;
    window.open(url, '_blank');
  };

  const handleGetSupplies = async () => {
    if (!printer.ip) return;

    setLoadingSupplies(true);
    setSuppliesError(null);

    try {
      const response = await axiosInstance.get(`/api/impresoras/get-supplies/${printer.ip}`, {
        timeout: 30000 // 30 segundos timeout
      });

      if (response.data.success) {
        setSupplies(response.data.supplies);
        setSuppliesError(null);
        setShowSupplies(true);
      } else {
        setSuppliesError(response.data.error || 'SNMP no disponible');
      }
    } catch (error) {
      console.error('Error fetching supplies:', error);
      if (error.code === 'ECONNABORTED') {
        setSuppliesError('Timeout - La impresora no responde a SNMP');
      } else {
        setSuppliesError('SNMP no disponible o impresora no responde');
      }
    } finally {
      setLoadingSupplies(false);
    }
  };

  const toggleSupplies = () => {
    if (!showSupplies && supplies.length === 0) {
      handleGetSupplies();
    } else {
      setShowSupplies(!showSupplies);
    }
  };

  // Función para detectar el color de la tinta basándose en el nombre
  const detectInkColor = (name) => {
    if (!name) return null;

    const lowerName = name.toLowerCase();

    if (lowerName.includes('cyan') || lowerName.includes('cian')) {
      return 'cyan';
    } else if (lowerName.includes('magenta')) {
      return 'magenta';
    } else if (lowerName.includes('yellow') || lowerName.includes('amarillo')) {
      return 'yellow';
    } else if (lowerName.includes('black') || lowerName.includes('negro')) {
      return 'black';
    }

    return null;
  };

  // Construir la URL de la foto basándose en marca y modelo de la impresora
  const getPhotoUrl = (extension = 'png') => {
    if (!printer.marca || !printer.modelo) return null;
    // Construir el nombre del archivo: "Marca Modelo.png" o "Marca Modelo.jpg"
    const fileName = `${printer.marca} ${printer.modelo}.${extension}`;
    // Codificar la URL para manejar espacios y caracteres especiales
    const encodedFileName = encodeURIComponent(fileName);
    return `/printer-photos/${encodedFileName}`;
  };

  const [photoUrl, setPhotoUrl] = React.useState(getPhotoUrl('png'));
  const [showPlaceholder, setShowPlaceholder] = React.useState(false);

  const handleImageError = (e) => {
    // Si la imagen PNG no se encuentra, intentar con JPG
    if (e.target.src.endsWith('.png')) {
      const jpgUrl = getPhotoUrl('jpg');
      setPhotoUrl(jpgUrl);
    } else {
      // Si tampoco existe JPG, mostrar el placeholder
      e.target.style.display = 'none';
      setShowPlaceholder(true);
    }
  };

  return (
    <div className="printer-card">
      <div className="printer-card-header">
        <div className="printer-card-photo">
          {photoUrl && !showPlaceholder ? (
            <img
              src={photoUrl}
              alt={printer.modelo}
              onError={handleImageError}
            />
          ) : null}
          <FaPrint
            className="printer-card-photo-placeholder"
            style={{ display: photoUrl && !showPlaceholder ? 'none' : 'flex' }}
          />
        </div>
        <div className="printer-card-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <BrandLogo brand={printer.marca} />
            <span className="printer-card-name" title={printer.nombre}>
              {printer.nombre}
            </span>
          </div>
          <span className="printer-card-brand">{printer.marca} {printer.modelo}</span>
        </div>
      </div>

      <div className="printer-card-body">
        <p>
          <strong>Sede:</strong>
          <span
            className="badge-sede"
            style={{
              backgroundColor: sedeColors.bg,
              color: sedeColors.color,
              borderColor: sedeColors.border
            }}
          >
            <FaMapMarkerAlt />
            {printer.sede || 'Sin asignar'}
          </span>
        </p>

        <div className="printer-card-divider"></div>

        <p>
          <strong>IP:</strong>
          {printer.ip ? (
            <span className="ip-link" onClick={handleIpClick} title="Click para abrir configuración">
              {printer.ip}
              <FaExternalLinkAlt />
            </span>
          ) : (
            <span>N/A</span>
          )}
        </p>

        <p>
          <strong>Serial:</strong>
          <CopyableField value={printer.serial} icon={<FaBarcode />} />
        </p>

        <div className="printer-card-divider"></div>

        <p>
          <strong>Contador:</strong>
          <div className="contador-info">
            <div className="contador-value">
              <EditableField
                value={printer.contador}
                onSave={(newValue) => onSave(printer.id, 'contador', newValue)}
              />
              <button
                className={`sync-button ${isSyncing ? 'syncing' : ''}`}
                onClick={handleSyncCounter}
                disabled={isSyncing}
                title="Sincronizar contador"
              >
                <FaSync />
              </button>
            </div>
            {printer.fechacontador && (
              <span className="fecha-contador">
                Actualizado: {new Date(printer.fechacontador).toLocaleDateString('es-ES')}
              </span>
            )}
          </div>
        </p>

        <div className="printer-card-divider"></div>

        <div className="provider-info">
          <strong>Proveedor:</strong>
          {printer.proveedor ? (
            <>
              <img
                src={`/brand-logos/${printer.proveedor.toLowerCase()}.png`}
                alt={`${printer.proveedor} logo`}
                className="provider-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
              <span style={{ display: 'none' }}>{printer.proveedor}</span>
            </>
          ) : (
            <span>Sin asignar</span>
          )}
        </div>

        <div className="printer-card-divider"></div>

        <div className="supplies-section">
          <button
            className={`supplies-button ${loadingSupplies ? 'loading' : ''}`}
            onClick={toggleSupplies}
            disabled={loadingSupplies}
            title="Ver estado de consumibles"
          >
            <FaTint />
            <span>Consumibles</span>
            {loadingSupplies ? (
              <FaSync className="spinning" />
            ) : showSupplies ? (
              <FaChevronUp />
            ) : (
              <FaChevronDown />
            )}
          </button>

          {showSupplies && (
            <div className="supplies-list">
              {suppliesError ? (
                <div className="supplies-error">{suppliesError}</div>
              ) : supplies.length > 0 ? (
                supplies.map((supply, index) => {
                  const inkColor = detectInkColor(supply.name);
                  const colorClass = inkColor || supply.status;
                  const isAvailable = supply.percentage !== null && supply.percentage !== undefined;

                  return (
                    <div key={index} className="supply-item">
                      <div className="supply-header">
                        <span className="supply-name">{supply.name}</span>
                        <span className={`supply-percentage ${colorClass}`}>
                          {isAvailable ? `${supply.percentage}%` : 'No disponible'}
                        </span>
                      </div>
                      {isAvailable ? (
                        <div className="supply-bar-container">
                          <div
                            className={`supply-bar ${colorClass}`}
                            style={{ width: `${supply.percentage}%` }}
                          ></div>
                        </div>
                      ) : (
                        <div className="supply-unavailable-message">
                          Nivel no reportado (posible cartucho no original)
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="supplies-empty">SNMP no disponible en esta impresora</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrinterCard;